import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Mail } from "lucide-react";
import Logo, { Brand } from "@/components/Logo";

export const BETA_EMAIL = "contact@peopleiknow.ai";
export const BETA_MAILTO =
  "mailto:contact@peopleiknow.ai?subject=Beta%20access%20request%20%E2%80%94%20PeopleiKnow&body=Hi%20PeopleiKnow%20team%2C%0A%0AI%27d%20love%20to%20join%20the%20beta.%20Here%27s%20a%20little%20about%20me%3A%0A%0A";

const NAV = [
  { to: "/privacy", label: "Privacy" },
  { to: "/security", label: "Security" },
  { to: "/terms", label: "Terms" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/60 shadow-sm"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label="PeopleiKnow home">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-blue-700" : "text-slate-600 hover:text-blue-700"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="https://app.peopleiknow.ai"
            data-testid="header-signin-cta"
            className="text-sm font-semibold text-slate-700 hover:text-blue-700 transition-colors"
          >
            Sign in
          </a>
          <a
            href="https://app.peopleiknow.ai/signup"
            data-testid="header-signup-cta"
            className="inline-flex items-center gap-2 bg-blue-700 text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-blue-800 transition-colors shadow-sm active:scale-95"
          >
            Sign up for free
          </a>
        </div>

        <button
          className="md:hidden p-2 -mr-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl"
          data-testid="mobile-nav"
        >
          <nav className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href="https://app.peopleiknow.ai"
              onClick={() => setOpen(false)}
              data-testid="mobile-signin-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-white text-blue-700 border border-blue-200 rounded-lg px-5 py-3 text-base font-semibold hover:bg-blue-50 transition-colors"
            >
              Sign in
            </a>
            <a
              href="https://app.peopleiknow.ai/signup"
              onClick={() => setOpen(false)}
              data-testid="mobile-signup-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-blue-700 text-white rounded-lg px-5 py-3 text-base font-semibold hover:bg-blue-800 transition-colors"
            >
              Sign up for free
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer
      className="border-t border-slate-200 bg-white mt-24"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo />
            <p className="mt-4 text-sm text-slate-500 max-w-xs leading-relaxed">
              Private relationship intelligence for the people who matter — in
              your professional and personal life.
            </p>
            <span
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-700"
              data-testid="footer-beta-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              Currently in development &amp; beta testing
            </span>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-semibold text-slate-900">Pages</h4>
            <ul className="mt-4 space-y-3">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-link-${item.label.toLowerCase()}`}
                    className="text-sm text-slate-500 hover:text-blue-700 transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-sm font-semibold text-slate-900">Get in touch</h4>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed">
              Beta access, partnerships, or privacy &amp; security questions —
              we read every email.
            </p>
            <a
              href={`mailto:${BETA_EMAIL}`}
              data-testid="footer-email-link"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800 transition-colors"
            >
              <Mail className="h-4 w-4" />
              {BETA_EMAIL}
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} <Brand />
            <sup data-testid="footer-trademark">TM</sup>. All rights reserved.
          </p>
          <p className="text-xs text-slate-400">
            Made for humans who hate losing touch.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />
      <main className="flex-1 pt-16">{children}</main>
      <Footer />
    </div>
  );
}
